/// <amd-module name="Test/ManageTestPlans/Components/DirectoryView" />
import * as React from "react";
import { TestPlanDirectoryActionsCreator } from "Test/ManageTestPlans/Actions/TestPlanDirectoryActionsCreator";
import { AllTestPlanComponent } from "Test/ManageTestPlans/Components/AllTestPlanComponent";
import { HubErrorBar } from "Test/ManageTestPlans/Components/HubErrorBar";
import { MyTestPlanComponent } from "Test/ManageTestPlans/Components/MyTestPlanComponent";
import { isAdvancedTestExtensionEnabled } from "Test/ManageTestPlans/LicenseUtils";
import * as Resources from "Test/ManageTestPlans/Resources";
import { TestPlanDirectoryStore } from "Test/ManageTestPlans/Stores/TestPlanDirectoryStore";
import { DirectoryPivotType, Filters, TcmPerfScenarios, TestPlanRouteParameters } from "Test/ManageTestPlansCommon/Constants";
import { IDirectoryPivot } from "Test/ManageTestPlansCommon/Contracts";
import { ITestPlanFilterManager } from "Test/ManageTestPlansFilter/Contracts";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Hub } from "VSSUI/Components/Hub/Hub";
import { HubHeader } from "VSSUI/Components/HubHeader/HubHeader";
import { IObservableViewStateUrl } from "VSSUI/Navigation/ViewStateNavigation";
import { IVssHubViewState, VssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { IPivotBarAction, PivotBarItem } from "VSSUI/PivotBar";
import { IFilterState } from "VSSUI/Utilities/Filter";
import { VssIconType } from "VSSUI/VssIcon";
import { isFeatureEnabled } from "VSS/Platform/Feature";

export interface ITestPlanDirectoryViewProps {
    selectedPivot: DirectoryPivotType;
    pivots: IDirectoryPivot[];
}

export class DirectoryView extends VssComponent<ITestPlanDirectoryViewProps, {}, IVssComponentContext> {
    constructor(props: ITestPlanDirectoryViewProps, context: IVssComponentContext) {
        super(props);
        this._pageContext = context.pageContext;
        this._hubViewState = new VssHubViewState(context.pageContext, {
            pivotNavigationParamName: TestPlanRouteParameters.Pivot,
            defaultPivot: DirectoryPivotType.mine
        });
        TestPlanDirectoryActionsCreator.getInstance().init(context.pageContext);
        this._actionsCreator = TestPlanDirectoryActionsCreator.getInstance();
        this._hubViewState.selectedPivot.subscribe(this._onPivotChanged);

        this._allPivotUrl = this._hubViewState.createObservableUrl({
            [TestPlanRouteParameters.Pivot]: DirectoryPivotType.all
        });
        this._minePivotUrl = this._hubViewState.createObservableUrl({
            [TestPlanRouteParameters.Pivot]: DirectoryPivotType.mine
        });
    }

    public componentDidMount() {
        super.componentDidMount();

        this._store.initializeCurrentPivot(this.props.selectedPivot);

        this._store.initializeFilterManager(this._onGetFilterManager(), this._pageContext, this._hubViewState, this._onFilterChanged);

        //  Initialize pivot's data so the store contains the initial filter state, which is needed
        //  to render the filter's icon in the hub header.
        switch (this.props.selectedPivot) {
            case DirectoryPivotType.all:
                this._actionsCreator.initializeAllTestPlan();
                break;
            case DirectoryPivotType.mine:
                this._actionsCreator.initializeMineTestPlan();
                break;
            default:
        }
    }

    private _onFilterChanged = (currentPivot: DirectoryPivotType, filterState: IFilterState) => {
        this._actionsCreator.filterPivotItems(currentPivot, filterState);
    };

    public render() {
        const pivotItems: any[] = [];

        this.props.pivots.forEach((pivotProp) => {
            const pivotType: string = pivotProp.type;
            pivotItems.push(
                <PivotBarItem
                    className={"test-plans-pivot"}
                    key={`pivotBarItem.${pivotProp.name}`}
                    name={pivotProp.name}
                    url={pivotProp.type === DirectoryPivotType.all ? this._allPivotUrl : this._minePivotUrl}
                    itemKey={pivotType}
                >
                    {pivotProp.type === DirectoryPivotType.all ? (
                        <AllTestPlanComponent store={this._store} actionsCreator={this._actionsCreator} />
                    ) : (
                        <MyTestPlanComponent store={this._store} actionsCreator={this._actionsCreator} />
                    )}
                </PivotBarItem>
            );
        });
        const errorBar = <HubErrorBar />;

        return (
            <div className="testplan-directory-content">
                {errorBar}
                <Hub
                    className="manage-plans-hub"
                    hubViewState={this._hubViewState}
                    onRenderFilterBar={this._getFilterBar}
                    commands={this.getHubCommands()}
                >
                    <HubHeader title={Resources.TestPlansText} />
                    {pivotItems}
                </Hub>
            </div>
        );
    }

    private _getFilterBar = (): JSX.Element => {
        return (
            <WrappedComponent
                dependencies={["ms.vss-test-web.manage-test-plans-filter"]}
                wrappedType={"testPlanDirectoryFilterBar"}
                wrappedProps={{
                    fields:
                        this._hubViewState.selectedPivot.value === DirectoryPivotType.all ? Filters.AllTabFilterItems : Filters.MineTabFilterItems,
                    isLoading: true,
                    store: this._store,
                    filter: this._hubViewState.filter
                }}
            />
        );
    };

    private _onGetFilterManager(): Promise<ITestPlanFilterManager> {
        let contributionService: IVssContributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        return new Promise((resolve, reject) => {
            contributionService.getContributionAsync(this.c_filterComponent).then(
                () => {
                    let testPlanFilterManager: any = contributionService.executeCommandEx(this.c_filterComponent, {
                        serviceName: this.c_filterComponentService,
                        methodName: this.c_filterComponentInitMethod,
                        methodArguments: [this._hubViewState, this.props.selectedPivot, this._onFilterChanged]
                    });
                    resolve(testPlanFilterManager);
                },
                (error: Error) => {
                    reject(error);
                }
            );
        });
    }

    private getHubCommands(): IPivotBarAction[] {
        if (isAdvancedTestExtensionEnabled(this._pageContext)) {
            return [
                {
                    key: "new-test-plan",
                    name: Resources.NewTestPlanText,
                    important: true,
                    iconProps: { iconName: "CalculatorAddition", iconType: VssIconType.fabric },
                    onClick: this._onNewTestPlanClicked
                }
            ];
        } else {
            return [];
        }
    }

    private _onNewTestPlanClicked = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void => {
        const newTestPlanCreationUrl = this._getNewTestPlanCreationUrl();
        FastPageSwitch(this._pageContext, newTestPlanCreationUrl, true);
    };

    private _onPivotChanged = (newPivotKey: string) => {
        if (newPivotKey == DirectoryPivotType.all) {
            this._perfService.startScenario(TcmPerfScenarios.LoadTestPlansAll, false);
            this._perfService.addSplitTiming("all-test-plans-pivot-switch", TcmPerfScenarios.LoadTestPlansAll);
        } else if (newPivotKey == DirectoryPivotType.mine) {
            this._perfService.startScenario(TcmPerfScenarios.LoadTestPlansMine, false);
            this._perfService.addSplitTiming("mine-test-plans-pivot-switch", TcmPerfScenarios.LoadTestPlansMine);
        }

        this._actionsCreator.pivotSwitched(newPivotKey);
    };

    private _getNewTestPlanCreationUrl = () => {
        let testHubRoute: string = "ms.vss-test-web.test-plan-directory-route";
        if (isFeatureEnabled(this._pageContext, "ms.vss-test-web.testplans-hub-refresh-feature", false)) {
            testHubRoute = "ms.vss-test-web.test-new-testplan-page-directory-route-LWP";
        }
        const tfsPageData = this._pageContext.getService<ITfsPageService>("ITfsPageService").getData();

        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = tfsPageData && tfsPageData.project ? tfsPageData.project.name : "";
        routeValues["pivot"] = "new";
        return routeUrl(this.context.pageContext, testHubRoute, routeValues);
    };

    private get _perfService() {
        return this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    }
    private _hubViewState: IVssHubViewState;
    private _actionsCreator: TestPlanDirectoryActionsCreator = TestPlanDirectoryActionsCreator.getInstance();
    private _store: TestPlanDirectoryStore = TestPlanDirectoryStore.getInstance();
    private _allPivotUrl: IObservableViewStateUrl;
    private _minePivotUrl: IObservableViewStateUrl;
    private _pageContext: IVssPageContext;
    private readonly c_filterComponent: string = "ms.vss-test-web.manage-test-plans-filter";
    private readonly c_filterComponentService = "testPlanFilterManager";
    private readonly c_filterComponentInitMethod = "init";
}
